import React from 'react';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import type { SxProps, Theme } from '@mui/material';

import colors from '../../common/colors';
import TapTooltip from '../TapTooltip';

export interface LabelChipProps {
  /** Text shown inside the chip. */
  label: string;
  /** Background (swatch) color — typically a backend-provided hex. */
  color: string;
  /** Optional hover tooltip. Ignored when `truncate` is set. */
  description?: string;
  /** Text color. Defaults to white. */
  textColor?: string;
  /**
   * When true, truncates the label with an ellipsis and reveals the full text
   * in a tooltip on hover or tap. Defaults to false (chip sizes to its label).
   */
  truncate?: boolean;
  /** Extra styles merged onto the chip. */
  sx?: SxProps<Theme>;
}

// Shrink-to-fit tap target that bounds the chip so its label truncates rather
// than overflowing the cell.
const TRUNCATE_WRAPPER_SX: SxProps<Theme> = {
  display: 'inline-flex',
  minWidth: 0,
  maxWidth: '100%',
};

/**
 * Small coloured pill: a swatch-coloured MUI Chip with a text label and an
 * optional tooltip. Accepts an arbitrary colour string so it can render
 * backend-driven label colours.
 */
const LabelChip = ({
  label,
  color,
  description,
  textColor = colors.white,
  truncate = false,
  sx,
}: LabelChipProps) => {
  const chip = (
    <Chip
      size="small"
      label={label}
      sx={{
        backgroundColor: color,
        color: textColor,
        fontWeight: 600,
        ...(truncate && { maxWidth: '100%', minWidth: 0 }),
        ...sx,
      }}
    />
  );

  // Controlled tooltip so a tap works on touch devices (which don't fire hover).
  if (truncate) {
    return (
      <TapTooltip title={label} sx={TRUNCATE_WRAPPER_SX}>
        {chip}
      </TapTooltip>
    );
  }

  if (!description) {
    return chip;
  }

  return <Tooltip title={description}>{chip}</Tooltip>;
};

export default LabelChip;
